import React, { useCallback, useMemo, useState } from "react";
import { Column, Row } from "@/shared/ui/Blocks";
import { Icon, IconName } from "@/shared/ui/Icon";
import { IconButton } from "@/shared/ui/IconButton";
import { SectionHeader } from "@/shared/ui/SectionHeader";
import { Button } from "@/shared/ui/Button";
import { DropdownMenu, DropdownMenuItem } from "../DropdownMenu";
import { cn } from "@/shared/utils/shadcn";

export interface DataItemProps {
  type?: "file" | "score";
  label?: string;
  tagLabel?: string;
  icon?: IconName;
  removable?: boolean;
  order?: number;
  onRemove?: () => void;
  onClick?: () => void;
  IconComponent?: React.ReactNode;
  className?: string;
  menuItems?: DropdownMenuItem[];
}

interface DataSectionProps {
  title?: string;
  description?: string;
  items: DataItemProps[];
  canEdit?: boolean;
  addableItems?: DataItemProps[];
  RightComponent?: React.ReactNode;
}

export function DataItem(props: DataItemProps) {
  const {
    type,
    label,
    tagLabel,
    icon,
    removable,
    onRemove,
    onClick,
    IconComponent,
    className,
    menuItems,
  } = props;

  const LabelComponent = useMemo(() => {
    return (
      <>
        {IconComponent}
        {icon && <Icon icon={icon} size="sm" className="mt-1 shrink-0" />}
        <Column className="gap-2 w-full min-w-0">
          <span className="truncate">{label}</span>
          {!!tagLabel && (
            <div>
              <span className="rounded-full px-2 bg-grey-100 text-xs text-grey-600 min-h-10">
                {tagLabel}
              </span>
            </div>
          )}
        </Column>
      </>
    );
  }, [label, tagLabel, icon, IconComponent]);

  return (
    <Row
      className={cn(
        "min-h-[89px] items-center py-4 border-b border-grey-300 border-solid overflow-hidden",
        !!onClick && "cursor-pointer hover:shadow-sm",
        className
      )}
      onClick={onClick}
    >
      <Row className="flex-1 gap-2 min-w-0">{LabelComponent}</Row>
      {(removable || !!menuItems?.length) && (
        <Column>
          <DropdownMenu
            align="end"
            items={
              menuItems || [
                {
                  label: "Remove",
                  icon: "Delete",
                  onClick: onRemove,
                },
              ]
            }
            trigger={
              <IconButton
                icon="KebabVertical"
                size="sm"
                variant="ghost"
                color="black"
              />
            }
          />
        </Column>
      )}
    </Row>
  );
}

export const DataSection: React.FC<DataSectionProps> = ({
  title,
  description,
  items,
  canEdit,
  addableItems,
  RightComponent,
}) => {
  const [showAll, setShowAll] = useState(false);

  const visibleItems = showAll ? items : items.slice(0, 6);

  const SectionRightComponent = useCallback(() => {
    if (RightComponent) return RightComponent;

    if (!addableItems?.length) return null;

    return (
      <div>
        <DropdownMenu
          items={addableItems as DropdownMenuItem[]}
          trigger={
            <IconButton
              label="Add data"
              icon="Plus"
              variant="secondary"
              size="md"
              color="blue"
            />
          }
        />
      </div>
    );
  }, [items]);

  return (
    <Column className={cn("gap-4")}>
      {!!title && (
        <SectionHeader
          title={title}
          subtitle={description}
          className=" mb-0"
          RightComponent={<SectionRightComponent />}
        />
      )}
      <div className="grid grid-cols-2 gap-4">
        {visibleItems.map((item, index) => (
          <DataItem
            key={`${item.type}-${item.label}-${index}`}
            {...item}
            removable={canEdit && item.removable}
          />
        ))}
      </div>
      {items.length > 6 && (
        <Row className="justify-center">
          <Button
            onClick={() => setShowAll(!showAll)}
            variant="ghost"
            size="sm"
          >
            {showAll ? "View Less" : "View More"}
          </Button>
        </Row>
      )}
    </Column>
  );
};
